import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Layout } from '@/components/layout'
import { useIndexedFoldersStore } from '@/lib/stores/indexedFoldersStore'
import { useHasPermission } from '@/lib/hooks/usePermission'
import { FolderConfigForm } from '../components/FolderConfigForm'
import { FolderConfigTable } from '../components/FolderConfigTable'
import { Button } from '@/components/ui/button'
import { RefreshCw, FolderOpen } from 'lucide-react'
import { useToast } from '@/lib/hooks/useToast'
import { type IndexedFolderConfig } from '@/lib/api/indexedFoldersApi'

export function IndexedFoldersManagementPage() {
  const { t } = useTranslation()
  const { toast } = useToast()
  const [isRefreshing, setIsRefreshing] = useState(false)

  const {
    folders,
    selectedFolderId,
    isLoading,
    error,
    fetchFolders,
    selectFolderForEdit,
    clearError,
    startIndexing,
  } = useIndexedFoldersStore()

  const canTriggerIndexing = useHasPermission('documents.refetch')
  const safeFolders = useMemo(() => folders ?? [], [folders])

  const selectedFolder = useMemo<IndexedFolderConfig | null>(() => {
    if (!selectedFolderId) {
      return null
    }

    return safeFolders.find(folder => folder?.id === selectedFolderId) ?? null
  }, [safeFolders, selectedFolderId])

  // Fetch folders on mount
  useEffect(() => {
    void fetchFolders().catch(() => undefined)
  }, [fetchFolders])

  // Clear error when component unmounts
  useEffect(() => {
    return () => {
      clearError()
    }
  }, [clearError])

  const handleReindexAll = async () => {
    try {
      await startIndexing()
      toast({
        title: t('planvorming.indexingStarted'),
        description: t('planvorming.indexingStartedDescription'),
      })
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error instanceof Error ? error.message : t('common.unknownError'),
        variant: 'destructive',
      })
    }
  }

  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      await fetchFolders()
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error instanceof Error ? error.message : t('common.unknownError'),
        variant: 'destructive',
      })
    } finally {
      setIsRefreshing(false)
    }
  }

  const handleEditFolder = (folder: IndexedFolderConfig) => {
    selectFolderForEdit(folder.id)
  }

  return (
    <Layout>
      <div className="flex flex-col gap-6 p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg shrink-0">
              <FolderOpen className="w-6 h-6 text-blue-600" aria-hidden="true" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">{t('admin.indexedFolders.title')}</h1>
              <p className="text-muted-foreground text-sm mt-0.5">
                {t('admin.indexedFolders.description')}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 shrink-0 w-full sm:w-auto">

            <Button
              onClick={handleRefresh}
              disabled={isRefreshing || isLoading}
              variant="outline"
              className="flex items-center gap-2 min-h-11"
            >
              <RefreshCw className={`w-4 h-4 ${(isRefreshing || isLoading) ? 'animate-spin' : ''}`} aria-hidden="true" />
              {t('common.refresh')}
            </Button>

            {canTriggerIndexing && (
              <Button
                onClick={handleReindexAll}
                disabled={isLoading || isRefreshing || safeFolders.length === 0}
                variant="outline"
                className="flex items-center gap-2 min-h-11"
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} aria-hidden="true" />
                {t('admin.indexedFolders.reindexAll')}
              </Button>
            )}
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div
            role="alert"
            className="bg-red-50 text-red-800 px-4 py-3 rounded-lg border border-red-200"
          >
            {error}
          </div>
        )}

        {/* Main content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-testid="indexed-folders-grid">
          {/* Form - Left column (2/5) */}
          <div className="lg:col-span-2" data-testid="indexed-folders-form-column">
            <FolderConfigForm selectedFolder={selectedFolder ?? null} />
          </div>

          {/* Table - Right column (3/5) */}
          <div className="lg:col-span-3" data-testid="indexed-folders-table-column">
            <FolderConfigTable
              folders={safeFolders}
              onEditFolder={handleEditFolder}
              isLoading={isLoading && safeFolders.length === 0}
            />
          </div>
        </div>
      </div>
    </Layout>
  )
}
